import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function ProductRow({ p, adminKey, onSaved }) {
  const [form, setForm] = useState({
    visible: p.visible,
    featured: p.featured,
    seasonal_tag: p.seasonal_tag || "",
    sort_order: p.sort_order === 9999 ? "" : p.sort_order,
    custom_title: p.custom_title || "",
    custom_description: p.custom_description || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.saveMeta(p.printful_product_id, {
        visible: form.visible,
        featured: form.featured,
        seasonal_tag: form.seasonal_tag,
        sort_order: form.sort_order === "" ? 9999 : Number(form.sort_order),
        custom_title: form.custom_title,
        custom_description: form.custom_description,
      }, adminKey);
      toast.success(`SAVED — ${p.printful_name.toUpperCase()}`);
      onSaved();
    } catch {
      toast.error("Couldn't save. Check your key.");
    } finally {
      setSaving(false);
    }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="bg-white shadow-print-sm p-5" data-testid={`admin-product-${p.printful_product_id}`}>
      <div className="flex items-start gap-4">
        <img src={p.images[0]} alt="" className="w-16 h-16 object-contain bg-paper shrink-0" />
        <div className="min-w-0 flex-1">
          <p className="font-college text-lg text-navy truncate">{p.printful_name}</p>
          <p className="text-xs text-ink/50">${p.price} · {p.variants.length} variant{p.variants.length > 1 ? "s" : ""} · PF #{p.printful_product_id}</p>
        </div>
        <button
          data-testid={`admin-save-${p.printful_product_id}`}
          onClick={save}
          disabled={saving}
          className="bg-navy text-paper font-college text-base px-4 py-2 shadow-print-sm hover:bg-red transition-colors disabled:opacity-60 shrink-0"
        >
          {saving ? "SAVING…" : "SAVE"}
        </button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid={`admin-visible-${p.printful_product_id}`} checked={form.visible} onChange={(e) => set("visible", e.target.checked)} className="w-4 h-4 accent-[#1F3A6E]" />
          VISIBLE ON SITE
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" data-testid={`admin-featured-${p.printful_product_id}`} checked={form.featured} onChange={(e) => set("featured", e.target.checked)} className="w-4 h-4 accent-[#C4281C]" />
          FEATURED ON HOMEPAGE
        </label>
        <input
          data-testid={`admin-seasonal-${p.printful_product_id}`}
          value={form.seasonal_tag}
          onChange={(e) => set("seasonal_tag", e.target.value)}
          placeholder="Seasonal tag e.g. FALL 26"
          className="bg-paper px-3 py-2 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red uppercase"
        />
        <input
          data-testid={`admin-sort-${p.printful_product_id}`}
          type="number"
          value={form.sort_order}
          onChange={(e) => set("sort_order", e.target.value)}
          placeholder="Sort order (1 = first)"
          className="bg-paper px-3 py-2 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red"
        />
        <input
          data-testid={`admin-title-${p.printful_product_id}`}
          value={form.custom_title}
          onChange={(e) => set("custom_title", e.target.value)}
          placeholder="Custom display title (optional)"
          className="bg-paper px-3 py-2 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red sm:col-span-2"
        />
        <input
          data-testid={`admin-desc-${p.printful_product_id}`}
          value={form.custom_description}
          onChange={(e) => set("custom_description", e.target.value)}
          placeholder="Short website description (optional)"
          className="bg-paper px-3 py-2 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red sm:col-span-2"
        />
      </div>
    </div>
  );
}

export default function ShopAdmin() {
  const [key, setKey] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [products, setProducts] = useState([]);
  const [subs, setSubs] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const load = async (k) => {
    const list = await api.adminProducts(k);
    setProducts(list);
    api.subscribers(k).then(setSubs).catch(() => {});
  };

  const unlock = async (e) => {
    e.preventDefault();
    try {
      await load(key);
      setUnlocked(true);
    } catch {
      toast.error("Wrong key.");
    }
  };

  const refresh = async () => {
    setSyncing(true);
    try {
      const r = await api.sync(key);
      toast.success(`SYNCED ${r.synced} PRODUCTS FROM PRINTFUL.`);
      await load(key);
    } catch {
      toast.error("Sync failed. Try again.");
    } finally {
      setSyncing(false);
    }
  };

  if (!unlocked) {
    return (
      <div className="max-w-md mx-auto px-5 pt-24 pb-10" data-testid="shop-admin-gate">
        <p className="font-college text-lg text-red">KEEP THIS PAGE TO YOURSELF</p>
        <h1 className="font-college text-4xl text-navy mt-2">SHOP MANAGER</h1>
        <form onSubmit={unlock} className="mt-8 flex gap-3">
          <input
            data-testid="shop-admin-key-input"
            type="password"
            required
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Admin key"
            className="flex-1 min-w-0 bg-white px-3 py-2.5 text-sm shadow-print-sm focus:outline-none focus:ring-2 focus:ring-red"
          />
          <button data-testid="shop-admin-unlock" className="bg-navy text-paper font-college text-lg px-5 shadow-print hover:bg-red transition-colors">
            OPEN
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-5 pt-14 pb-10" data-testid="shop-admin-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="font-college text-lg text-red">PUBLISHED IN PRINTFUL — CONTROLLED HERE</p>
          <h1 className="font-college text-4xl sm:text-5xl text-navy mt-2" data-testid="shop-admin-heading">SHOP MANAGER</h1>
          <p className="text-sm text-ink/60 mt-2 max-w-lg">
            Products come from your Printful store. Publish or unpublish there; control how they appear here.
            Your tags and settings survive every refresh.
          </p>
        </div>
        <button
          data-testid="shop-admin-refresh"
          onClick={refresh}
          disabled={syncing}
          className="bg-red text-paper font-college text-lg px-5 py-3 shadow-print hover:bg-navy transition-colors disabled:opacity-60"
        >
          {syncing ? "SYNCING…" : "REFRESH FROM PRINTFUL"}
        </button>
      </div>
      <div className="space-y-5 mt-10">
        {products.map((p) => (
          <ProductRow key={p.printful_product_id} p={p} adminKey={key} onSaved={() => load(key)} />
        ))}
        {products.length === 0 && <p className="text-ink/50">No published products found in Printful.</p>}
      </div>

      <div className="mt-16" data-testid="admin-subscribers-section">
        <h2 className="font-college text-3xl text-navy">GET THE NOTE — SIGNUPS ({subs.length})</h2>
        <div className="bg-white shadow-print-sm mt-5 divide-y divide-ink/10">
          {subs.map((s) => (
            <div key={s.email} className="flex flex-wrap justify-between gap-2 px-5 py-3 text-sm">
              <span data-testid={`subscriber-${s.email}`}>{s.email}</span>
              <span className="text-ink/50">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleString() : ""}</span>
            </div>
          ))}
          {subs.length === 0 && <p className="px-5 py-4 text-sm text-ink/50">No signups yet.</p>}
        </div>
      </div>
    </div>
  );
}
